"use client";
import { useState } from "react";
import { StandardProps } from "../../componentMap";
import { useVariant } from "@/hooks/useVariant/useVariant.ts";
import { isAvailableToOrder } from "@/utils/product/isAvailableToOrder.ts";
import { GridGalleryUI } from "./GridGalleryUI";
import { GalleryContextProvider } from "../context/GalleryContext";
import {
  hasSocialProofMessages,
  videoPreloadValues,
  SocialProofMessagesProperties,
} from "@/components/Galleries/utils.ts";
import { parseFlagEnumValue } from "@/utils/flags/parseFlagEnumValue.ts";

export const GridGallery = (
  props: Pick<
    StandardProps,
    | "product"
    | "configProps"
    | "flags"
    | "experiencesRawObject"
    | "runtimeConfig"
  > & { sizes: string },
) => {
  const {
    product,
    sizes,
    configProps,
    flags,
    experiencesRawObject,
    runtimeConfig,
  } = props;
  const { variant } = useVariant(product);

  const [showSocialProofMessages, setShowSocialProofMessages] = useState(
    flags.showSocialProofMessages.value === true,
  );

  const videoPreload = parseFlagEnumValue(
    String(flags.videoPreload?.value),
    videoPreloadValues,
    "auto",
  );

  const pdpAsLanding = configProps?.pdpAsLanding === true;

  if (!variant) return null;

  const media = "media" in product ? product.media : null;

  const videos = media?.videos || [];

  const title = variant.title;

  const images = variant.images;

  const spm = hasSocialProofMessages(configProps)
    ? configProps.SocialProofMessages
    : undefined;
  const socialProofMessagesProperties: SocialProofMessagesProperties =
    product.__typename === "SimpleProduct" && spm
      ? {
          appId: spm.appId ?? "",
          withClose: spm.withClose || true,
          productId: product.aliases.pimProductId,
          brand: product.brand.name,
          availableToOrder: isAvailableToOrder(product),
          experiencesRawObject,
          showSocialProofMessages: showSocialProofMessages,
          setShowSocialProofMessages,
          monetateEnvironment: runtimeConfig.monetateEnvironment,
          reduceSocialProofMessagesPersistenceScroll:
            flags.reduceSocialProofMessagesPersistenceScroll.value === true,
        }
      : undefined;

  const amendCarouselZoomIcon = flags.amendCarouselZoomIcon?.value;

  return (
    <GalleryContextProvider {...props}>
      <GridGalleryUI
        title={title}
        images={images}
        videos={videos}
        sizes={sizes}
        hasSocialProofMessagesExperience={true}
        socialProofMessagesProperties={socialProofMessagesProperties}
        hasSuperZoom={!flags.isSuperZoomDisabled.value}
        usePrefetching={!!flags.usePrefetching.value}
        useResponsiveImages={!!flags.useResponsiveImages.value}
        pdpAsLanding={pdpAsLanding}
        videoPreload={videoPreload}
        carouselGalleryInpTest={flags.carouselGalleryInpTest.value === true}
        galleryImageZoomOnHover={flags.galleryImageZoomOnHover.value === true}
        experiencesRawObject={experiencesRawObject}
        runtimeConfig={runtimeConfig}
        amendCarouselZoomIcon={amendCarouselZoomIcon}
      />
    </GalleryContextProvider>
  );
};
